/**
 * Os anexos e os links de UMA conversa — carregamento, agrupamento por mês e
 * os formatadores que as duas telas usam.
 *
 * POR QUE É UM COMPOSABLE
 *
 * A mesma informação aparece em DOIS lugares: a linha "Mídia, links e docs" no
 * card do contato (que só mostra o total) e o painel que abre no lugar do card
 * (que mostra tudo). Se cada um tivesse a sua própria busca, o contador da
 * linha e o das abas divergiriam — foi exatamente esse tipo de cópia que gerou
 * três bugs no dia 2026-08-07 ("tinha que ter paridade e interligações, pra não
 * ficar pontas soltas").
 *
 * Aqui a busca é uma só; a linha lê `total`, o painel lê as listas.
 */
import { ref, computed } from 'vue';
import ConversationApi from 'dashboard/api/conversations';

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

// "Junho de 2026". O ano entra sempre: uma conversa de dois anos teria dois
// "Junho" e ninguém saberia qual é qual.
export const rotuloDoMes = timestamp => {
  const d = new Date(timestamp * 1000);
  return `${MESES[d.getMonth()]} de ${d.getFullYear()}`;
};

// A lista já vem ordenada do servidor (mais novos primeiro); aqui só se agrupa,
// preservando essa ordem.
export const agruparPorMes = itens => {
  const grupos = [];
  itens.forEach(item => {
    const rotulo = rotuloDoMes(item.created_at);
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.rotulo === rotulo) ultimo.itens.push(item);
    else grupos.push({ rotulo, itens: [item] });
  });
  return grupos;
};

export const formatarTamanho = bytes => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// O endpoint não devolve o nome do arquivo — só a URL. O nome está no fim dela,
// e vem percent-encoded (`RG%20ADRIANA.pdf`).
export const nomeDoArquivo = anexo => {
  try {
    const caminho = new URL(anexo.data_url).pathname;
    return (
      decodeURIComponent(caminho.split('/').pop()) || anexo.extension || ''
    );
  } catch {
    return anexo.extension || '';
  }
};

export const iconePorTipo = anexo => {
  if (anexo.file_type === 'audio') return 'i-lucide-mic';
  if (anexo.file_type === 'video') return 'i-lucide-video';
  const ext = String(anexo.extension || '').toLowerCase();
  if (ext === 'pdf') return 'i-lucide-file-text';
  if (['doc', 'docx'].includes(ext)) return 'i-lucide-file-type';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'i-lucide-table';
  return 'i-lucide-paperclip';
};

// O domínio é o que a pessoa reconhece na lista de links; a URL inteira não
// cabe e todas começam iguais.
export const dominioDoLink = url => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const ehMidia = a => ['image', 'video'].includes(a.file_type);
const ehAudio = a => a.file_type === 'audio';

export function useAnexosDaConversa(conversationId) {
  const anexos = ref([]);
  const links = ref([]);
  const carregando = ref(false);
  const falhou = ref(false);
  const total = ref(0);

  // TRÊS categorias de anexo: Mídias, Áudios e Docs.
  //
  // O ÁUDIO TINHA ABA PRÓPRIA REJEITADA e voltou atrás em 21/08/2026, com o
  // problema materializado e medido. O comentário anterior aqui registrava o
  // risco ("uma conversa real tinha 49 anexos, sendo 47 áudios") e apostava
  // que reordenar Docs bastaria. Não bastou: o Eduardo Seibert relatou que os
  // áudios atrapalham achar documento, e a base inteira confirma o motivo —
  //
  //   aba Docs mostrava 1.788 itens: 1.490 áudios (83%) contra 292 arquivos.
  //   conversa 3262: 67 áudios para 7 arquivos. conversa 3245: 44 para 6.
  //
  // Reordenar não resolveria: a lista é agrupada por MÊS, então arquivo-antes-
  // de-áudio só ajuda dentro de cada mês — com 3 meses de conversa, ainda se
  // rola por dezenas de áudios. Áudio é a MAIOR categoria da base (maior que
  // imagem); não é "documento" por definição nenhuma, e merece lugar próprio.
  const midias = computed(() => anexos.value.filter(ehMidia));
  const audios = computed(() => anexos.value.filter(ehAudio));
  const documentos = computed(() =>
    anexos.value.filter(a => !ehMidia(a) && !ehAudio(a))
  );

  const idAtual = () =>
    typeof conversationId === 'function' ? conversationId() : conversationId;

  const carregar = async () => {
    const id = idAtual();
    if (!id) return;
    carregando.value = true;
    falhou.value = false;
    try {
      // Em paralelo: são endpoints independentes e o painel só está pronto
      // quando os dois chegam.
      const [respostaAnexos, respostaLinks] = await Promise.all([
        ConversationApi.attachments(id),
        // Links são extra: se essa rota falhar, as outras duas abas continuam.
        ConversationApi.links(id).catch(() => ({ data: { payload: [] } })),
      ]);
      // A pessoa pode ter trocado de conversa enquanto a busca andava.
      if (id !== idAtual()) return;
      anexos.value = respostaAnexos.data.payload || [];
      links.value = respostaLinks.data.payload || [];
      total.value =
        (respostaAnexos.data.meta?.total_count ?? anexos.value.length) +
        links.value.length;
    } catch {
      if (id !== idAtual()) return;
      // Sem alerta por cima da tela — anexo é conveniência, não pode
      // atrapalhar o atendimento. Mas o painel DIZ que falhou: antes ele
      // mostrava "Nenhuma mídia", e quem procurava o RG da cliente concluía
      // que ela nunca tinha mandado.
      anexos.value = [];
      links.value = [];
      total.value = 0;
      falhou.value = true;
    } finally {
      if (id === idAtual()) carregando.value = false;
    }
  };

  const limpar = () => {
    falhou.value = false;
    anexos.value = [];
    links.value = [];
    total.value = 0;
  };

  return {
    anexos,
    links,
    midias,
    audios,
    documentos,
    carregando,
    falhou,
    total,
    carregar,
    limpar,
  };
}

// OS ANEXOS DE UM CONTATO (05/10/2026): os mesmos do painel da conversa,
// somados nas conversas dele — a aba Mídia da ficha do contato usa o mesmo
// painel (Mídias / Áudios / Docs / Links). As conversas mais recentes
// primeiro; `limite` segura o número de buscas em contato com centenas.
export function useAnexosDoContato(idsDasConversas, { limite = 15 } = {}) {
  const anexos = ref([]);
  const links = ref([]);
  const carregando = ref(false);
  const falhou = ref(false);

  const midias = computed(() => anexos.value.filter(ehMidia));
  const audios = computed(() => anexos.value.filter(ehAudio));
  const documentos = computed(() =>
    anexos.value.filter(a => !ehMidia(a) && !ehAudio(a))
  );

  const carregar = async () => {
    const ids = (
      typeof idsDasConversas === 'function'
        ? idsDasConversas()
        : idsDasConversas
    ).slice(0, limite);
    carregando.value = true;
    falhou.value = false;
    try {
      const respostas = await Promise.all(
        ids.map(id =>
          Promise.all([
            ConversationApi.attachments(id),
            ConversationApi.links(id).catch(() => ({ data: { payload: [] } })),
          ])
        )
      );
      anexos.value = respostas.flatMap(([a]) => a.data.payload || []);
      links.value = respostas.flatMap(([, l]) => l.data.payload || []);
    } catch {
      anexos.value = [];
      links.value = [];
      falhou.value = true;
    } finally {
      carregando.value = false;
    }
  };

  return { midias, audios, documentos, links, carregando, falhou, carregar };
}
